import { Bot, BotIntent, BotVariable } from '../models/bot.model';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { PortalService } from './portal.service';

@Injectable({
  providedIn: 'root',
})
export class BotService {
  constructor(private http: HttpClient) { }
  
  createBot(name: string, template: string = ''): Observable<any> {
    return this.http.post(PortalService.ApiUrl + '/bot', {
      name,
      template,
      template_data: {
        sales_dialog: [],
      },
    });
  }

  getBot(id: string): Observable<any> {
    return this.http.get<Bot>(PortalService.ApiUrl + '/bot/' + id);
  }

  updateBot(bot: Bot): Observable<any> {
    return this.http.put(PortalService.ApiUrl + '/bot/' + bot.bot_id, bot);
  }

  updateBotActivity(id: string, value: boolean): Observable<any> {
    return this.http.post(PortalService.ApiUrl + '/bot/active/' + id, {
      active: value,
    });
  }

  updateBotSettings(id: string, settings: any): Observable<any> {
    return this.http.post(
      PortalService.ApiUrl + '/bot/settings/' + id,
      settings
    );
  }

  updateBotImage(id: string, fileData: any): Observable<any> {
    const formData = new FormData(),
      headers = new HttpHeaders();
    formData.append('file', fileData);
    headers.set('Content-Type', 'multipart/form-data');
    return this.http.post(PortalService.ApiUrl + '/bot/image/' + id, formData, {
      headers,
    });
  }

  createBotConnection(
    accountId: string,
    botId: string,
    provider: string,
    providerData: any
  ): Observable<any> {
    return this.http.post(
      PortalService.ApiUrl + '/connection/' + provider + '/' + botId,
      {
        account_id: accountId,
        provider_data: providerData,
      }
    );
  }

  getBotConnections(id: string): Observable<any> {
    return this.http.get(PortalService.ApiUrl + '/connections/' + id);
  }

  updateBotConnection(
    accountId: string,
    botId: string,
    provider: string,
    providerData: any
  ): Observable<any> {
    return this.http.put(
      PortalService.ApiUrl + '/connection/' + provider + '/' + botId,
      {
        account_id: accountId,
        provider_data: providerData,
      }
    );
  }

  updateBotTemplate(botId: string, template: any): Observable<any> {
    return this.http.put(
      PortalService.ApiUrl + '/bot/template/' + botId,
      template
    );
  }

  getBotVariables(id: string): Observable<any> {
    return this.http.get(PortalService.ApiUrl + '/variables/' + id);
  }

  updateBotVariable(botId: string, variable: BotVariable): Observable<any> {
    return this.http.put(
      PortalService.ApiUrl + '/variable/' + botId + '/' + variable.name,
      variable
    );
  }

  getBotIntents(id: string): Observable<any> {
    const params = new HttpParams().append('limit', '1000');
    return this.http.get(PortalService.ApiUrl + '/intents/' + id, {
      params,
    });
  }

  updateBotIntent(intent: BotIntent): Observable<any> {
    return this.http.put(
      PortalService.ApiUrl + '/intent/' + intent.intent_id,
      intent
    );
  }

  createBotShopifyTool(botId: string, shopId: string): Observable<any> {
    return this.http.post(
      PortalService.ApiUrl + '/tool/' + botId + '/shopify',
      {
        properties: {
          shop_id: shopId,
        },
      }
    );
  }

  getBotMessageBuffer(id: string): Observable<any> {
    return this.http.get(
      PortalService.ApiUrl + '/tool/' + id + '/message-buffer'
    );
  }

  createBotMessageBuffer(botId: string, period: string): Observable<any> {
    return this.http.post(
      PortalService.ApiUrl + '/tool/' + botId + '/message-buffer',
      {
        properties: {
          period,
        },
      }
    );
  }

  updateBotMessageBuffer(botId: string, period: string): Observable<any> {
    return this.http.put(
      PortalService.ApiUrl + '/tool/' + botId + '/message-buffer',
      {
        properties: {
          period,
        },
      }
    );
  }

  getBots(): Observable<any> {
    return this.http.get(PortalService.ApiUrl + '/bots');
  }

  getBotsByPortal(portalId: string): Observable<any> {
    return this.http.get(PortalService.ApiUrl + '/bots?portal_id=' + portalId);
  }

  getBotFilters(id: string): Observable<any> {
    return this.http.get(PortalService.ApiUrl + '/clients/filters/' + id);
  }

  getBotScenarios(id: string): Observable<any> {
    return this.http.get(PortalService.ApiUrl + '/scenarios/' + id);
  }

  getBotCollections(id: string): Observable<any> {
    return this.http.get(PortalService.ApiUrl + '/collections/' + id);
  }

  getBotCategories(id: string): Observable<any> {
    return this.http.get(PortalService.ApiUrl + '/categories/' + id);
  }

  getBotProducts(id: string): Observable<any> {
    return this.http.get(PortalService.ApiUrl + '/products/' + id);
  }

  getBotTags(id: string): Observable<any> {
    return this.http.get(PortalService.ApiUrl + '/tags/' + id);
  }

  updateBotTag(id: string, tag: any): Observable<any> {
    return this.http.put(`${PortalService.ApiUrl}/tag/${tag.tag_id}`, {
      name: tag.name,
      color: tag.color,
      bot_id: id,
    });
  }

  getBotMails(id: string): Observable<any> {
    return this.http.get(PortalService.ApiUrl + '/mails/' + id);
  }

  getBotEntities(id: string): Observable<any> {
    return this.http.get(PortalService.ApiUrl + '/entities/' + id + '?system=true');
  }

  // Dashboard
  getBotTeam(botId?: string): Observable<any> {
    return this.http.get(PortalService.ApiUrl + '/bot/team/' + botId);
  }

  getDashboardStatistics(botId?: string, params?: any): Observable<any> {
    return this.http.get(
      PortalService.ApiUrl + '/report/bot/statistics/' + botId,
      { params }
    );
  }
}
